import { doc, setDoc, getDoc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export async function createTryOnSession(sessionId, personImageUrl, products, userInfo = null) {
  const sessionRef = doc(db, 'tryOnSessions', sessionId);

  const sessionData = {
    sessionId,
    createdAt: serverTimestamp(),
    personImageUrl: `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${extractStoragePath(personImageUrl)}`,
    personImagePublicUrl: personImageUrl,
    userName: userInfo?.userName || 'Guest User',
    results: products.map(product => ({
      productId: product.id,
      productName: product.name,
      productImagePaths: product.imagePaths || [product.imagePath],
      size: product.size,
      price: product.price,
      tryOnImageUrl: null,
      tryOnImagePublicUrl: null,
      status: 'processing',
      error: null
    })),
    status: 'processing'
  };

  // Add user info fields if provided
  if (userInfo) {
    sessionData.userContact = userInfo.userContact;
    sessionData.consentGiven = userInfo.consentGiven;
  }

  await setDoc(sessionRef, sessionData);
}

/**
 * Extract storage path from public URL
 * @param {string} publicUrl - Public storage URL
 * @returns {string} - Storage path (just the filename/path)
 */
function extractStoragePath(publicUrl) {
  // Extract path from URLs like:
  // Emulator: http://localhost:9199/v0/b/{bucket}/o/{path}?alt=media
  // Production: https://storage.googleapis.com/{bucket}/{path}
  // gs:// format: gs://{bucket}/{path}

  console.log('Extracting storage path from:', publicUrl);

  if (publicUrl.includes('localhost:9199')) {
    // Emulator format: http://localhost:9199/v0/b/{bucket}/o/{encodedPath}?alt=media
    const match = publicUrl.match(/\/o\/([^?]+)/);
    if (match) {
      const decoded = decodeURIComponent(match[1]);
      console.log('Extracted from emulator URL:', decoded);
      return decoded;
    }
  }

  if (publicUrl.includes('storage.googleapis.com') || publicUrl.includes('firebasestorage.app')) {
    // Production format: https://storage.googleapis.com/{bucket}/{path}
    // Or: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
    const match = publicUrl.match(/\/o\/([^?]+)/) || publicUrl.match(/googleapis\.com\/[^/]+\/(.+)$/);
    if (match) {
      const decoded = decodeURIComponent(match[1]);
      console.log('Extracted from production URL:', decoded);
      return decoded;
    }
  }

  if (publicUrl.startsWith('gs://')) {
    // gs://bucket/path format - extract just the path
    const match = publicUrl.match(/^gs:\/\/[^/]+\/(.+)$/);
    const path = match ? match[1] : publicUrl.replace(/^gs:\/\/[^/]+\//, '');
    console.log('Extracted from gs:// URL:', path);
    return path;
  }

  // If no match, assume it's already just a path
  console.log('No match, using as-is:', publicUrl);
  return publicUrl;
}

/**
 * Get try-on session from Firestore
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - Session data
 */
export async function getTryOnSession(sessionId) {
  const sessionRef = doc(db, 'tryOnSessions', sessionId);
  const sessionSnap = await getDoc(sessionRef);

  if (!sessionSnap.exists()) {
    throw new Error('Session not found');
  }

  return sessionSnap.data();
}

/**
 * Subscribe to real-time updates for a try-on session
 * @param {string} sessionId - Session ID
 * @param {Function} callback - Callback function to receive updates
 * @returns {Function} - Unsubscribe function
 */
export function subscribeTryOnSession(sessionId, callback) {
  const sessionRef = doc(db, 'tryOnSessions', sessionId);

  return onSnapshot(sessionRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data());
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error listening to session:', error);
    callback(null, error);
  });
}

export async function updateTryOnSessionWithUserInfo(sessionId, userInfo) {
  const sessionRef = doc(db, 'tryOnSessions', sessionId);

  await updateDoc(sessionRef, {
    userName: userInfo.userName,
    userContact: userInfo.userContact,
    consentGiven: userInfo.consentGiven,
    userInfoUpdatedAt: serverTimestamp()
  });
}

/**
 * Generate unique session ID
 * @returns {string} - Unique session ID
 */
export function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
